import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Heart, Phone, Facebook, Instagram, Youtube, HandHeart } from 'lucide-react';
import { XLogo } from './XLogo';
import { NAV, SITE } from '../data/mock';
import { cn } from '../lib/utils';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { api } from '../lib/api';

// Adapt DB menu-tree to the mock NAV shape (label, path, submenu, children, target)
function adaptMenuTree(tree) {
  return (tree || []).map(node => ({
    label: node.label,
    path: node.url || '/',
    target: node.open_in_new_tab ? '_blank' : undefined,
    submenu: (node.children || []).length > 0
      ? node.children.map(c => ({
          label: c.label,
          path: c.url || '/',
          target: c.open_in_new_tab ? '_blank' : undefined,
          children: (c.children || []).map(g => ({
            label: g.label,
            path: g.url || '/',
            target: g.open_in_new_tab ? '_blank' : undefined,
          })),
        }))
      : undefined,
  }));
}

export default function Header() {
  const settings = usePublicSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [subHover, setSubHover] = useState(null);
  const [nav, setNav] = useState(NAV);
  const [subHoverTop, setSubHoverTop] = useState(0);
  const location = useLocation();

  useEffect(() => {
    // Try loading dynamic menu from DB; fall back to mock silently
    api.publicMenu().then(tree => {
      if (Array.isArray(tree) && tree.length > 0) setNav(adaptMenuTree(tree));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden md:block bg-ink-700 text-cream/90 text-xs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href={`tel:${(settings.site_phone || SITE.phone || '').replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"><Phone className="w-3.5 h-3.5" /> {settings.site_phone || SITE.phone}</a>
            <span className="opacity-60"> | </span>
            <a href={`mailto:${settings.site_email || SITE.email}`} className="hover:text-gold transition-colors">{settings.site_email || SITE.email}</a>
          </div>
          <div className="flex items-center gap-4">
            {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-gold transition-colors"><Facebook className="w-3.5 h-3.5" /></a>}
            {settings.social_twitter && <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="hover:text-gold transition-colors"><XLogo className="w-3.5 h-3.5" /></a>}
            {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-gold transition-colors"><Instagram className="w-3.5 h-3.5" /></a>}
            {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-gold transition-colors"><Youtube className="w-3.5 h-3.5" /></a>}
            <span className="mx-1 h-4 w-px bg-cream/20" />
            {(() => {
              const panelUrl = (settings.panel_button_url || '/admin/login').trim();
              const panelLabel = settings.panel_button_label || 'Panel';
              const cls = 'inline-flex items-center gap-1.5 border border-gold/50 text-gold hover:bg-gold hover:text-ink-700 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest transition-colors';
              return panelUrl.startsWith('http')
                ? <a href={panelUrl} target="_blank" rel="noopener noreferrer" className={cls} data-testid="panel-btn">{panelLabel}</a>
                : <Link to={panelUrl} className={cls} data-testid="panel-btn">{panelLabel}</Link>;
            })()}
          </div>
        </div>
      </div>

      <header
        className={cn(
          'sticky top-0 z-50 transition-all duration-300',
          scrolled ? 'bg-cream/90 backdrop-blur-md shadow-[0_1px_0_0_rgba(15,30,77,0.08)]' : 'bg-cream'
        )}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-24 lg:h-28 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="YEKÜV Ana Sayfa">
            <img
              src="/logos/yekuv-logo.png"
              alt="YEKÜV - 21. Yüzyıl Eğitim ve Kültür Vakfı"
              className="h-16 lg:h-20 w-auto object-contain select-none transition-transform group-hover:scale-[1.03]"
              draggable="false"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => { setHover(null); setSubHover(null); }}>
            {nav.map((item, i) => (
              <div key={i} className="relative" onMouseEnter={() => { setHover(i); setSubHover(null); }}>
                <MenuLink to={item.path} target={item.target} className={cn(
                  'px-4 py-2.5 text-[14px] font-medium rounded-full inline-flex items-center gap-1 transition-colors',
                  location.pathname === item.path ? 'text-ink-600 bg-ink-50' : 'text-ink-500 hover:text-ink-600 hover:bg-ink-50/60'
                )}>
                  {item.label}
                  {item.submenu && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
                </MenuLink>
                {item.submenu && hover === i && (
                  <div className="absolute left-0 top-full pt-2 w-80 z-40">
                    <div className="relative bg-white rounded-2xl shadow-2xl shadow-ink-900/10 border border-ink-100 p-2 fade-up">
                      <div className="max-h-[70vh] overflow-y-auto pr-1">
                        {item.submenu.map((s, j) => {
                          const hasKids = (s.children || []).length > 0;
                          return (
                            <div key={j} onMouseEnter={(e) => {
                              if (hasKids) {
                                // Align 3rd-level flyout with the hovered row's Y offset (relative to panel container)
                                const rowEl = e.currentTarget;
                                const panelEl = rowEl.closest('.relative');
                                const top = panelEl ? rowEl.getBoundingClientRect().top - panelEl.getBoundingClientRect().top : 0;
                                setSubHoverTop(top);
                                setSubHover(j);
                              } else {
                                setSubHover(null);
                              }
                            }}>
                              <MenuLink to={s.path} target={s.target}
                                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm text-ink-500 hover:bg-ink-50 hover:text-ink-600 transition-colors">
                                <span>{s.label}</span>
                                {hasKids && <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                              </MenuLink>
                            </div>
                          );
                        })}
                      </div>
                      {/* 3rd-level flyout — sibling to scroll container so it can escape overflow */}
                      {subHover !== null && item.submenu[subHover] && (item.submenu[subHover].children || []).length > 0 && (
                        <div className="absolute left-full pl-2 w-72 z-50" style={{ top: subHoverTop }}>
                          <div className="bg-white rounded-2xl shadow-2xl shadow-ink-900/10 border border-ink-100 p-2 max-h-[70vh] overflow-y-auto">
                            {item.submenu[subHover].children.map((g, k) => (
                              <MenuLink key={k} to={g.path} target={g.target}
                                className="block px-4 py-2.5 rounded-xl text-sm text-ink-500 hover:bg-ink-50 hover:text-ink-600 transition-colors">
                                {g.label}
                              </MenuLink>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <Link
              to="/gonullu"
              className="hidden sm:inline-flex items-center gap-2 bg-transparent border-2 border-ink-600 text-ink-600 px-5 lg:px-6 h-11 lg:h-12 rounded-full text-sm font-semibold hover:bg-ink-600 hover:text-cream transition-colors"
              data-testid="header-volunteer-btn"
            >
              <HandHeart className="w-4 h-4" />
              Gönüllü Ol
            </Link>
            <Link
              to="/bagis"
              className="hidden sm:inline-flex items-center gap-2 bg-ink-600 text-cream px-5 lg:px-6 h-11 lg:h-12 rounded-full text-sm font-semibold hover:bg-ink-500 transition-colors shadow-lg shadow-ink-900/20"
            >
              <Heart className="w-4 h-4 fill-coral-300 text-coral-300" />
              Bağış Yap
            </Link>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2.5 text-ink-600 rounded-full hover:bg-ink-50" aria-label="Menü">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-ink-100 bg-cream max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col">
              {nav.map((item, i) => (
                <MobileNavItem key={i} item={item} />
              ))}
              <Link to="/gonullu" className="py-3 text-ink-500">Gönüllü Ol</Link>
              <MenuLink
                to={(settings.panel_button_url || '/admin/login').trim()}
                target={(settings.panel_button_url || '').startsWith('http') ? '_blank' : undefined}
                className="py-3 text-ink-400 text-sm"
              >
                {settings.panel_button_label || 'Panel'}
              </MenuLink>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

// Menu link that opens in new tab OR uses react-router Link based on target/protocol.
function MenuLink({ to, target, className, children }) {
  const isExternal = /^https?:\/\//i.test(to || '') || target === '_blank';
  if (isExternal) {
    return <a href={to} target={target || '_blank'} rel="noopener noreferrer" className={className}>{children}</a>;
  }
  return <Link to={to || '/'} className={className}>{children}</Link>;
}

// Mobile accordion item — top level with expandable submenu (and 3rd-level children).
function MobileNavItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const hasSub = Array.isArray(item.submenu) && item.submenu.length > 0;

  if (!hasSub) {
    return <MenuLink to={item.path} target={item.target} className="py-3 text-ink-600 border-b border-ink-100 font-medium">{item.label}</MenuLink>;
  }

  return (
    <div className="border-b border-ink-100">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-3 text-ink-600 font-medium"
        data-testid={`mobile-nav-${item.label}`}
        aria-expanded={expanded}
      >
        {item.label}
        <ChevronDown className={cn('w-4 h-4 text-ink-400 transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="pb-3 pl-4 flex flex-col gap-0.5">
          {item.submenu.map((sub, i) => {
            const hasChildren = Array.isArray(sub.children) && sub.children.length > 0;
            return (
              <div key={i}>
                <MenuLink to={sub.path} target={sub.target} className="block py-2 text-sm text-ink-500 hover:text-ink-600">{sub.label}</MenuLink>
                {hasChildren && (
                  <div className="pl-4 flex flex-col border-l border-ink-100">
                    {sub.children.map((g, j) => (
                      <MenuLink key={j} to={g.path} target={g.target} className="block py-1.5 text-[13px] text-ink-400 hover:text-ink-600">{g.label}</MenuLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
